import React from "react";
import {FileClock, Link2, LucideIcon, Unlink, UserPen} from "lucide-react";
import {BidDeveloperData, BidLevelingLink} from "../bidding_model";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "../../ui/tooltip";
import {moneyFormatter} from "../../../lib/formatters";

const money = (v?: number | null) => v != null ? moneyFormatter.format(v) : "-";

/**
 * cell reference - links to the cell in excel online when we have the workbook url.
 * activeCell is what excel for the web uses to select a cell on open.
 */
function Cell({link, cell}: { link: BidLevelingLink, cell?: string | null }) {
    if (!cell) return <>-</>;
    if (!link.web_url) return <>{cell}</>;
    const url = `${link.web_url}&activeCell=${encodeURIComponent(`'${link.sheet_name}'!${cell}`)}`;
    return (
        <a href={url} target="_blank" rel="noreferrer" className="underline"
           // the icon sits inside the bid row's link - don't open the bid too
           onClick={(e) => e.stopPropagation()}>
            {cell}
        </a>
    );
}

type LinkState = { Icon: LucideIcon, color: string, label: string, differs?: boolean };

/**
 * which state to show - first match wins (see backend/leveling - resolve_leveled)
 *   lost        - column not found in the bid tab on last sync. needs a look
 *   manual      - leveled by hand, sync does not overwrite it. red + ≠ when the workbook total differs
 *   behind      - workbook base bid does not match this bid (eg revision not entered yet)
 *   linked      - leveled amount is the workbook total
 */
function linkState(developer: BidDeveloperData, link: BidLevelingLink): LinkState {
    if (link.lost) {
        return {Icon: Unlink, color: "text-red-600", label: "not found in bid tab on last sync"};
    }
    if (developer.leveling_status === "leveled") {
        const differs = link.leveled_amount != null && link.leveled_amount !== developer.leveled_amount;
        return differs
            ? {Icon: UserPen, color: "text-red-600", differs,
               label: `leveled by hand - workbook total is ${money(link.leveled_amount)}`}
            : {Icon: UserPen, color: "text-blue-600", label: "leveled by hand"};
    }
    if (!link.matched) {
        return {Icon: FileClock, color: "text-amber-500",
                label: `bid tab not updated for this bid - base ${money(link.base_bid_amount)}`};
    }
    return {Icon: Link2, color: "text-green-600", label: "from bid tab"};
}

/**
 * Bid tab workbook link state + hover with the cells (linked to excel online).
 * nothing shown for a bid that is not linked.
 */
export default function LinkInfo({developer}: { developer: BidDeveloperData }) {
    const link = developer.link;
    if (!link) return null;
    const {Icon, color, label, differs} = linkState(developer, link);

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className={`inline-flex items-center ${color}`}>
                        <Icon className="h-3.5 w-3.5"/>
                        {differs && <span className="text-xs font-bold leading-none">≠</span>}
                    </span>
                </TooltipTrigger>
                <TooltipContent className="text-xs text-left">
                    <div className="font-semibold">
                        {link.sheet_name} - {link.title_value ?? "?"} (<Cell link={link} cell={link.title_cell}/>)
                    </div>
                    <div>base bid <Cell link={link} cell={link.base_bid_cell}/>: {money(link.base_bid_amount)}</div>
                    <div>total <Cell link={link} cell={link.total_cell}/>: {money(link.leveled_amount)}</div>
                    <div className={color}>{label}</div>
                    <div className="text-gray-400">
                        synced {link.last_synced_at ? new Date(link.last_synced_at).toLocaleString() : "never"}
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
